import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Download, ChevronRight, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { format } from "@/lib/brand-tokens";
import { paymentRuns, paymentItems, getAdminById, generatePaymentRun } from "@/lib/mock-data";
import { useStoreVersion } from "@/hooks/useStore";
import { Gated } from "@/components/Gated";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// ADM-09 — Payment runs list
export default function PaymentRunsPage() {
  useStoreVersion();
  const navigate = useNavigate();
  const [genOpen, setGenOpen] = useState(false);
  const [cutoff, setCutoff] = useState(new Date().toISOString().slice(0, 10));
  // Real eligible items derived from the live store: status=Eligible, not yet in a run.
  const eligibleItems = paymentItems.filter(p => p.status === "Eligible" && !p.runId);
  const eligibleCount = eligibleItems.length;
  const eligibleAmount = eligibleItems.reduce((s, p) => s + (p.amount || 0), 0);
  const eligibleMembers = new Set(eligibleItems.map(p => p.memberId)).size;

  const generate = () => {
    setGenOpen(false);
    if (eligibleCount === 0) {
      toast.info("No eligible payments", { description: "Approve cards to populate the next run." });
      return;
    }
    const cutoffISO = cutoff ? `${cutoff}T23:59:59.999Z` : null;
    const result = generatePaymentRun(cutoffISO);
    if (!result?.runId) {
      toast.info("Nothing to sweep", { description: "All eligible items are after the cutoff date." });
      return;
    }
    toast.success(`Payment run ${result.runId} generated`, {
      description: `${result.count} items totaling ${format.currency(result.total)}.`,
    });
    navigate(`/admin/payments/${result.runId}`);
  };

  const sorted = [...paymentRuns].sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt));

  return (
    <div data-testid="payment-runs-page" className="space-y-5 pb-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow">Payments</div>
          <h1 className="font-brand text-display text-ink leading-tight mt-1">Payment runs</h1>
          <p className="text-body-sm text-ink-muted mt-1">{sorted.length} runs · {format.currency(sorted.reduce((a, r) => a + r.totalAmount, 0))} processed lifetime</p>
        </div>
        <Gated action="payments.generateRun">
          <Button onClick={() => setGenOpen(true)} className="bg-brand-700 hover:bg-brand-800 text-white" data-testid="generate-run-btn">
            <Plus className="size-4 mr-1.5" /> Generate payment run
          </Button>
        </Gated>
      </div>

      <div className="-mx-8 bg-surface border-y border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-body-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left">
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint">Run</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Generated</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Cutoff</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Members</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Items</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint text-right">Total</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Status</th>
                <th className="px-3 py-3 text-eyebrow uppercase text-ink-hint">Exports</th>
                <th className="px-8 py-3 text-eyebrow uppercase text-ink-hint text-right"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line/60">
              {sorted.map((r) => {
                const generator = getAdminById(r.generatedBy);
                const items = paymentItems.filter((p) => p.runId === r.id);
                const itemCount = items.length || r.itemCount;
                return (
                  <tr key={r.id} className="hover:bg-surface-alt/40">
                    <td className="px-8 py-3.5">
                      <Link to={`/admin/payments/${r.id}`} className="font-mono text-code text-brand-700 hover:text-brand-800 font-semibold">{r.id}</Link>
                      <div className="text-caption text-ink-subtle mt-0.5">by {generator?.firstName} {generator?.lastName[0]}.</div>
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums whitespace-nowrap">
                      <div className="text-ink">{format.date(r.generatedAt)}</div>
                      <div className="text-ink-subtle">{format.relative(r.generatedAt)}</div>
                    </td>
                    <td className="px-3 py-3.5 text-ink-muted text-caption tabular-nums">{format.date(r.cutoffDate)}</td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{r.memberCount}</td>
                    <td className="px-3 py-3.5 text-ink tabular-nums text-right">{itemCount}</td>
                    <td className="px-3 py-3.5 text-ink font-semibold tabular-nums text-right">{format.currency(r.totalAmount)}</td>
                    <td className="px-3 py-3.5"><StatusBadge entity="paymentRun" status={r.status} dot /></td>
                    <td className="px-3 py-3.5 text-caption text-ink-muted">
                      <div>CSV ×{r.csvDownloads}</div>
                      <div>NACHA ×{r.nachaDownloads}</div>
                    </td>
                    <td className="px-8 py-3.5 text-right">
                      <Link to={`/admin/payments/${r.id}`} className="inline-flex items-center gap-1 text-caption font-semibold text-brand-700 hover:text-brand-800">
                        Open <ChevronRight className="size-3.5" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Generate run modal */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate payment run</DialogTitle>
            <DialogDescription>Bundle all eligible payments through the cutoff date into a single run.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-caption font-semibold text-ink mb-1.5 block">Cutoff date</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-ink-hint pointer-events-none" />
                <Input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value)} className="pl-9" />
              </div>
              <p className="mt-1.5 text-caption text-ink-hint">Includes payments approved on or before this date.</p>
            </div>
            <div className="bg-brand-50/60 border border-brand-100 rounded-md p-4 space-y-2.5">
              <div className="flex justify-between text-caption">
                <span className="text-ink-muted">Eligible items</span>
                <span className="text-ink font-semibold tabular-nums">{eligibleCount}</span>
              </div>
              <div className="flex justify-between text-caption">
                <span className="text-ink-muted">Members affected</span>
                <span className="text-ink font-semibold tabular-nums">{eligibleMembers}</span>
              </div>
              <div className="border-t border-brand-100 pt-2.5 flex justify-between">
                <span className="text-ink-muted text-caption">Total amount</span>
                <span className="font-brand text-h3 text-brand-700 tabular-nums font-bold">{format.currency(eligibleAmount)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={eligibleCount === 0} data-testid="generate-run-confirm-btn" className="bg-brand-700 hover:bg-brand-800 text-white disabled:opacity-50">Generate run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
